import * as React from 'react';
import { Mic, MicOff, Video as VideoIcon, VideoOff, Headphones, Loader2, X } from 'lucide-react';
import { useAuthStore } from '@/store/auth-store';
import { colorFromName, initialsFromName } from '@/components/ui-kit';
import { captureVoiceOwner } from '../voice-owner';
import { createVoicePreview,type VoicePreview } from '../voice-preview';
import { useVoiceSessionStore } from '../voice-session-store';
import { useChatDialogFocus } from '@/features/team-chat/hooks/useChatDialogFocus';
import { useTranslation } from '@/lib/i18n/react';
import { formatNumber } from '@/lib/i18n/runtime';
import { pickDevice } from '../pick-device';
import type { VoiceDevicePrefs } from '../voice-session-store';

interface VoiceLobbyProps {
  channelName?: string;
  onEnter: (prefs: VoiceDevicePrefs) => void;
  onCancel: () => void;
}

/**
 * Pre-join "lobby" for a voice channel (Zoom/Meet-style): preview your camera,
 * check your mic level, pick mic/camera/speaker, then join. Fully defensive —
 * if permissions or devices are unavailable the preview/meter just degrade and
 * "Unirse" still works (you join muted / audio-only). All media is torn down
 * before connecting so the room re-acquires cleanly.
 */
export function VoiceLobby({ channelName, onEnter, onCancel }: VoiceLobbyProps) {
  const {t}=useTranslation('voice');
  const dialogRef=React.useRef<HTMLDivElement>(null);
  const previewRef=React.useRef<VoicePreview|null>(null);
  const currentUser = useAuthStore((s) => s.user);

  const [mics, setMics] = React.useState<MediaDeviceInfo[]>([]);
  const [cams, setCams] = React.useState<MediaDeviceInfo[]>([]);
  const [speakers, setSpeakers] = React.useState<MediaDeviceInfo[]>([]);
  const [micId, setMicId] = React.useState<string | undefined>();
  const [camId, setCamId] = React.useState<string | undefined>();
  const [speakerId, setSpeakerId] = React.useState<string | undefined>();
  const [joinMuted, setJoinMuted] = React.useState(false);
  const [joinCamera, setJoinCamera] = React.useState(false);
  const [micLevel, setMicLevel] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [permError, setPermError] = React.useState(false);

  const videoElRef=React.useRef<HTMLVideoElement|null>(null);
  const refreshDevices=React.useCallback(async(preview:VoicePreview)=>{
    if(!preview.current())return;
    try{const list=await navigator.mediaDevices.enumerateDevices();if(!preview.current())return;
      const mi=list.filter(d=>d.kind==='audioinput'),ci=list.filter(d=>d.kind==='videoinput'),so=list.filter(d=>d.kind==='audiooutput');
      setMics(mi);setCams(ci);setSpeakers(so);setMicId(prev=>pickDevice(mi,prev));setCamId(prev=>pickDevice(ci,prev));setSpeakerId(prev=>pickDevice(so,prev));
    }catch{ /* Device labels are optional; use localized defaults. */ }
    if(preview.current())setLoading(false);
  },[]);
  React.useEffect(()=>{
    const preview=createVoicePreview(captureVoiceOwner(),useVoiceSessionStore.getState().revision);previewRef.current=preview;
    void Promise.resolve().then(()=>refreshDevices(preview));const changed=()=>{if(preview.current())void refreshDevices(preview);};navigator.mediaDevices?.addEventListener?.('devicechange',changed);
    return()=>{preview.dispose();navigator.mediaDevices?.removeEventListener?.('devicechange',changed);if(previewRef.current===preview)previewRef.current=null;};
  },[refreshDevices]);
  React.useEffect(()=>{
    const preview=previewRef.current;let retired=false,stream:MediaStream|null=null,ac:AudioContext|null=null,raf:number|undefined;
    if(!preview?.current())return;
    const release=preview.cleanup(()=>{retired=true;if(raf!==undefined)cancelAnimationFrame(raf);void ac?.close().catch(()=>{});if(stream)preview.stop(stream);});
    if(joinMuted)return release;
    void(async()=>{
      try{const value=await navigator.mediaDevices.getUserMedia({audio:micId?{deviceId:{exact:micId}}:true});
        if(retired||!preview.retain(value)){value.getTracks().forEach(track=>track.stop());return;}stream=value;setPermError(false);
        void refreshDevices(preview);
        try{const AC=window.AudioContext??(window as unknown as {webkitAudioContext:typeof AudioContext}).webkitAudioContext;ac=new AC();const source=ac.createMediaStreamSource(value),analyser=ac.createAnalyser();analyser.fftSize=256;source.connect(analyser);const data=new Uint8Array(analyser.frequencyBinCount);
          const tick=()=>{if(retired||!preview.current())return;analyser.getByteTimeDomainData(data);let sum=0;for(const point of data)sum+=((point-128)/128)**2;setMicLevel(Math.min(1,Math.sqrt(sum/data.length)*3));raf=requestAnimationFrame(tick);};tick();
        }catch{ /* Level meter is optional. */ }
      }catch{if(!retired&&preview.current())setPermError(true);}
    })();return release;
  },[micId,joinMuted,refreshDevices]);
  React.useEffect(()=>{
    const preview=previewRef.current;let retired=false,stream:MediaStream|null=null;
    if(!preview?.current()||!joinCamera)return;
    const video=videoElRef.current;
    const release=preview.cleanup(()=>{retired=true;if(stream)preview.stop(stream);if(video)video.srcObject=null;});
    void(async()=>{
      try{const value=await navigator.mediaDevices.getUserMedia({video:camId?{deviceId:{exact:camId}}:true});
        if(retired||!preview.retain(value)){value.getTracks().forEach(track=>track.stop());return;}stream=value;
        if(video){video.srcObject=value;void video.play().catch(()=>{});}
      }catch{if(!retired&&preview.current())setJoinCamera(false);}
    })();return release;
  },[joinCamera,camId]);
  const handleEnter=()=>{const preview=previewRef.current;if(!preview?.current())return;preview.dispose();onEnter({micId,cameraId:camId,speakerId,joinMuted:joinMuted||permError,joinCamera});};
  const handleCancel=()=>{const preview=previewRef.current;if(!preview?.current())return;preview.dispose();onCancel();};

  useChatDialogFocus(dialogRef,handleCancel);
  const name = currentUser?.name ?? t('you');

  return (
    <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="voice-lobby-title" className="fixed inset-0 z-[180] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 md:p-6">
      <div className="w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-gradient-to-b from-ink-900 to-[#0c0c12]">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="h-9 w-9 rounded-lg bg-blaze-green/15 text-blaze-green flex items-center justify-center flex-shrink-0">
              <Headphones size={17} />
            </span>
            <div className="min-w-0">
              <div id="voice-lobby-title" className="text-white font-bold text-sm leading-tight truncate">
                {t('joinTitle')} <bdi dir="auto">{channelName ?? t('roomTitle')}</bdi>
              </div>
              <div className="text-white/70 text-[11px]">{t('lobbyHint')}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            title={t('cancel')} aria-label={t('cancel')}
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Preview */}
          <div className="relative aspect-video rounded-xl overflow-hidden bg-white/[0.03] border border-white/5 flex items-center justify-center">
            {joinCamera ? (
              <video ref={videoElRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            ) : (
              <div
                className="h-20 w-20 rounded-full flex items-center justify-center text-white font-extrabold text-2xl overflow-hidden"
                style={{ background: currentUser?.avatar ? undefined : colorFromName(name) }}
              >
                {currentUser?.avatar ? (
                  <img src={currentUser.avatar} alt="" className="h-full w-full object-cover" />
                ) : (
                  initialsFromName(name)
                )}
              </div>
            )}
            {loading && (
              <span className="absolute top-2 end-2 text-white/70">
                <Loader2 size={16} className="animate-spin" />
              </span>
            )}
            {/* Mic level meter */}
            <div className="absolute bottom-2 start-2 end-2 flex items-center gap-2">
              <Mic size={13} className="text-white/70 flex-shrink-0" />
              <div role="meter" aria-label={t('previewMic')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={joinMuted?0:Math.round(micLevel*100)} className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-blaze-green transition-[width] duration-75"
                  style={{ width: `${Math.round((joinMuted?0:micLevel) * 100)}%` }}
                />
              </div>
            </div>
          </div>

          {permError && (
            <p role="alert" className="text-[11px] text-yellow-400/90 bg-yellow-400/10 rounded-lg px-3 py-2">
              {t('micBlocked')}
            </p>
          )}

          {/* Quick toggles */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setJoinMuted((m) => !m)}
              aria-pressed={!joinMuted}
              className={`flex-1 h-10 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold transition-colors ${
                joinMuted ? 'bg-red-500/90 text-white' : 'bg-white/10 hover:bg-white/15 text-white'
              }`}
            >
              {joinMuted ? <MicOff size={15} /> : <Mic size={15} />}
              {t(joinMuted?'micMuted':'micActive')}
            </button>
            <button
              type="button"
              onClick={() => setJoinCamera((c) => !c)}
              aria-pressed={joinCamera}
              className={`flex-1 h-10 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold transition-colors ${
                joinCamera ? 'bg-blaze-green text-ink-900' : 'bg-white/10 hover:bg-white/15 text-white'
              }`}
            >
              {joinCamera ? <VideoIcon size={15} /> : <VideoOff size={15} />}
              {t(joinCamera?'cameraOn':'cameraOff')}
            </button>
          </div>

          {/* Device selectors */}
          <div className="space-y-2">
            <DeviceSelect label={t('mic')} devices={mics} value={micId} onChange={setMicId} />
            {joinCamera && (
              <DeviceSelect label={t('camera')} devices={cams} value={camId} onChange={setCamId} />
            )}
            {speakers.length > 0 && (
              <DeviceSelect label={t('speaker')} devices={speakers} value={speakerId} onChange={setSpeakerId} />
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-white/5 flex justify-end gap-2">
          <button
            type="button"
            onClick={handleCancel}
            className="h-10 px-4 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={handleEnter}
            className="h-10 px-6 rounded-full bg-blaze-green hover:opacity-90 text-ink-900 text-sm font-bold transition-opacity"
          >
            {t('join')}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeviceSelect({
  label,
  devices,
  value,
  onChange,
}: {
  label: string;
  devices: MediaDeviceInfo[];
  value: string | undefined;
  onChange: (id: string) => void;
}) {
  const {t}=useTranslation('voice');
  return (
    <label className="block">
      <span className="text-white/70 text-[11px] font-semibold">{label}</span>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-9 rounded-lg bg-white/5 border border-white/10 text-white text-sm px-2 focus:outline-none focus:border-blaze-green"
      >
        {devices.length === 0 && <option value="">{t('defaultDevice')}</option>}
        {devices.map((d, i) => (
          <option key={d.deviceId || i} value={d.deviceId} className="bg-ink-900">
            {d.label || `${label} ${formatNumber(i + 1)}`}
          </option>
        ))}
      </select>
    </label>
  );
}
